import React, { createContext, useState, useContext, useEffect } from 'react';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { UserRole } from '../lib/types';

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  role: UserRole | null;
  companyId: string | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isCompanyAdmin: boolean;
  isDriver: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, fullName: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

function toAppUser(su: SupabaseUser): User {
  return {
    id: su.id,
    name: su.user_metadata?.full_name ?? su.email ?? 'User',
    email: su.email ?? '',
    avatar: su.user_metadata?.avatar_url,
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  async function loadProfile(uid: string) {
    const { data } = await supabase
      .from('profiles')
      .select('role, company_id')
      .eq('id', uid)
      .single();
    setRole((data?.role as UserRole) ?? 'operator');
    setCompanyId(data?.company_id ?? null);
  }

  async function refreshProfile() {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) await loadProfile(session.user.id);
  }

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setUser(toAppUser(session.user));
        setIsAuthenticated(true);
        await loadProfile(session.user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        setUser(toAppUser(session.user));
        setIsAuthenticated(true);
        await loadProfile(session.user.id);
      } else {
        setUser(null);
        setRole(null);
        setCompanyId(null);
        setIsAuthenticated(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return !error;
  };

  const register = async (
    email: string,
    password: string,
    fullName: string
  ): Promise<{ success: boolean; error?: string }> => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  if (loading) return null;

  const isSuperAdmin = role === 'superadmin';
  const isCompanyAdmin = role === 'admin';
  const isAdmin = role === 'admin' || role === 'operator' || role === 'superadmin';
  const isDriver = role === 'driver';

  return (
    <AuthContext.Provider value={{
      user, role, companyId, isAuthenticated,
      isSuperAdmin, isAdmin, isCompanyAdmin, isDriver,
      login, register, logout, refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
